import * as React from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

import { MessageSquare, Paperclip, History } from "lucide-react"
import CommentSection from "./CommentSection"
import AttachmentSection from "./AttachmentSection"
import ActivityLogSection from "./ActivityLogSection"

export interface RelatedTabItem {
    id: string
    label: string
    count?: number
    icon?: React.ReactNode
    content: React.ReactNode
}

export interface RelatedDataTabsProps {
    tabs?: RelatedTabItem[]
    defaultTab?: string
    className?: string
    entityType?: string
    entityId?: string
}

export function RelatedDataTabs({ tabs, defaultTab, className, entityType, entityId }: RelatedDataTabsProps) {
    const finalTabs: RelatedTabItem[] = tabs || [
        {
            id: 'comments',
            label: 'Thảo luận & Ghi chú',
            icon: <MessageSquare className="w-4 h-4" />,
            content: entityType && entityId ? (
                <div className="flex flex-col gap-6 p-4">
                    <CommentSection entityType={entityType} entityId={entityId} />
                </div>
            ) : <div className="p-4 text-center text-slate-500">Thiếu thông tin Entity</div>
        },
        {
            id: 'attachments',
            label: 'Tệp đính kèm',
            icon: <Paperclip className="w-4 h-4" />,
            content: entityType && entityId ? (
                <div className="p-4">
                    <AttachmentSection entityType={entityType} entityId={entityId} />
                </div>
            ) : <div className="p-4 text-center text-slate-500">Thiếu thông tin Entity</div>
        },
        {
            id: 'history',
            label: 'Lịch sử cập nhật',
            icon: <History className="w-4 h-4" />,
            content: entityType && entityId ? <ActivityLogSection entityType={entityType} entityId={entityId} /> : <div className="p-4 text-center text-slate-500">Thiếu thông tin Entity</div>
        }
    ]

    const [currentTab, setCurrentTab] = React.useState<string>(defaultTab || finalTabs[0]?.id || 'comments')

    React.useEffect(() => {
        if (defaultTab) {
            setCurrentTab(defaultTab);
        }
    }, [defaultTab]);

    React.useEffect(() => {
        const handleOpenHistory = () => {
            setCurrentTab('history');
            setTimeout(() => {
                const el = document.getElementById('entity-context-panel');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 50);
        };

        window.addEventListener('apv:open-history-tab', handleOpenHistory);
        return () => {
            window.removeEventListener('apv:open-history-tab', handleOpenHistory);
        };
    }, []);

    if (!finalTabs || finalTabs.length === 0) return null;

    return (
        <div className={`mt-6 ${className || ""}`}>
            <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
                <TabsList className="mb-4">
                    {finalTabs.map((tab) => (
                        <TabsTrigger
                            key={tab.id}
                            value={tab.id}
                            className="flex-none shrink-0"
                        >
                            <div className="flex items-center gap-1.5">
                                {tab.icon}
                                {tab.label}
                            </div>
                            {tab.count !== undefined && (
                                <Badge variant="secondary" className="ml-2 bg-slate-100 text-slate-600 font-normal">
                                    {tab.count}
                                </Badge>
                            )}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {finalTabs.map((tab) => (
                    <TabsContent key={tab.id} value={tab.id} className="mt-0 outline-none">
                        <Card className="border-slate-200 shadow-sm overflow-hidden">
                            <CardContent className="p-0">
                                {tab.content}
                            </CardContent>
                        </Card>
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    )
}

export default RelatedDataTabs;
